import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { getI18n } from 'client/AppReducer';
import { getUserId } from 'client/modules/Auth/AuthReducer';
import { setError } from 'client/modules/App/AppActions';
import callApi from 'client/util/apiCaller';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlans } from '../../PricingActions';
import { getPeriod, getPlans } from '../../PricingReducer';
import { formatPricingMessage } from '../../pricingMessages';
import {
  buildInitialVolumeIndices,
  findPlanForSelection,
  getAnnualSavingsMonths,
  getTierVolumeAtIndex,
  getTierVolumePrice,
  INTERVALS,
  TIERS,
} from '../../pricingCatalog';
import Spinner from '../../../../App/components/Spinner/Spinner';

const pricingTableSx = {
  margin: '4em auto',
  minHeight: 500,
  maxWidth: 1280,
};

const tierGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1.5rem',
  margin: '0 auto',
  maxWidth: 1200,
  alignItems: 'stretch',
};

const tierCardSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  bgcolor: 'background.paper',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  transition: 'box-shadow 0.2s ease',
};

const tierCardPopularSx = {
  borderColor: 'primary.main',
  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
};

const tierVolumeLabelsSx = {
  display: 'flex',
  justifyContent: 'space-between',
  mb: 0.5,
};

const tierVolumeLabelTextSx = {
  color: '#868682',
  fontSize: 12,
  fontWeight: 400,
  fontFamily: '"Figtree", sans-serif',
  lineHeight: 1.4,
};

const tierVolumeValueSx = {
  mt: 0.75,
  textAlign: 'center',
  fontSize: 14,
  color: '#5a5a56',
  fontFamily: '"Figtree", sans-serif',
};

const formatPeople = (people, formatNumber) => {
  if (people === null) {
    return null;
  }
  return formatNumber(people);
};

function TierPricingPage({
  embedded = false,
  changePlan = false,
  currentPlanId = null,
  onPlanChanged = null,
}) {
  const intl = useIntl();
  const { formatNumber } = intl;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(getUserId);
  const locale = useSelector(state => getI18n(state).locale);
  const plans = useSelector(getPlans);
  const plansPeriod = useSelector(getPeriod);
  const [interval, setInterval] = useState(INTERVALS.month);
  const [volumeIndices, setVolumeIndices] = useState(() => buildInitialVolumeIndices());
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const period = interval === INTERVALS.year ? '1y' : '1m';
  const [loadingPlans, setLoadingPlans] = useState(
    () => plans.length === 0 || plansPeriod !== period,
  );
  const [pendingPlan, setPendingPlan] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (plansPeriod === period && plans.length > 0) {
      setLoadingPlans(false);
      return;
    }
    setLoadingPlans(true);
    dispatch(fetchPlans(period)).finally(() => setLoadingPlans(false));
  }, [dispatch, period, plans.length, plansPeriod]);

  useEffect(() => {
    if (!changePlan || !currentPlanId || !plans.length) {
      return;
    }
    setVolumeIndices(buildInitialVolumeIndices(plans, currentPlanId));
  }, [changePlan, currentPlanId, plans]);

  const handleIntervalChange = (_, value) => {
    if (value) {
      setInterval(value);
    }
  };

  const handleTierVolumeChange = (tierId, value) => {
    setVolumeIndices(current => ({
      ...current,
      [tierId]: value,
    }));
  };

  const handleSelectTier = useCallback(
    (tier, people) => async () => {
      const pricing = getTierVolumePrice(tier, people, interval);
      if (!pricing || pricing.contact) {
        window.location.href =
          'mailto:hello@promoter.ninja?subject=Promoter%20Ninja%20Scale%20plan';
        return;
      }

      const plan = findPlanForSelection(plans, tier.id, people, interval);
      if (!plan) {
        dispatch(
          setError(
            <FormattedMessage
              id="Pricing.PlanNotAvailable"
              defaultMessage="This plan is not available yet. Please contact us."
            />,
          ),
        );
        return;
      }

      if (changePlan) {
        if (plan.id === currentPlanId) {
          return;
        }
        setPendingPlan(plan);
        setConfirmOpen(true);
        return;
      }

      if (!userId) {
        navigate('/sign-up');
        return;
      }

      setCheckoutLoading(tier.id);
      try {
        const response = await callApi(dispatch, 'users/checkout-pages', null, 'POST', {
          planId: plan.id,
          locale,
        });
        const url = response.url || response.hosted_page?.url;
        if (url) {
          window.location.href = url;
        } else {
          throw new Error('No checkout URL');
        }
      } catch (response) {
        if (response?.errors?.user === 'had_subscription') {
          dispatch(
            setError(
              <FormattedMessage
                id="Subscription.HadSubscription"
                defaultMessage="You already had an active subscription. Go to {subscriptionSettings}."
                values={{
                  subscriptionSettings: (
                    <Link to="/settings/billing">
                      <FormattedMessage
                        id="Subscription.SubscriptionSettings"
                        defaultMessage="subscription settings"
                      />
                    </Link>
                  ),
                }}
              />,
            ),
          );
        } else {
          dispatch(
            setError(
              <FormattedMessage
                id="Subscription.CreationError"
                defaultMessage="There was an error creating subscription, please try again."
              />,
            ),
          );
        }
      } finally {
        setCheckoutLoading(null);
      }
    },
    [changePlan, currentPlanId, dispatch, interval, locale, navigate, plans, userId],
  );

  const handleConfirmPlanChange = useCallback(async () => {
    if (!pendingPlan) {
      return;
    }
    setCheckoutLoading(pendingPlan?.tier || pendingPlan?.metadata?.tier || 'changing');
    setConfirmOpen(false);
    try {
      const result = await callApi(dispatch, 'users/subscriptions/change-plan', null, 'POST', {
        priceId: pendingPlan.id,
      });
      if (onPlanChanged) {
        onPlanChanged(result);
      }
    } catch (response) {
      if (response?.errors?.plan === 'legacy_subscription') {
        dispatch(
          setError(
            <FormattedMessage
              id="Billing.LegacyPlanChangeBlocked"
              defaultMessage="Plan changes are not available for legacy subscriptions. Please contact {support}."
              values={{
                support: <a href="mailto:support@promoter.ninja">support@promoter.ninja</a>,
              }}
            />,
          ),
        );
      } else {
        dispatch(
          setError(
            <FormattedMessage
              id="Billing.PlanChangeError"
              defaultMessage="Could not change plan. Please try again."
            />,
          ),
        );
      }
    } finally {
      setCheckoutLoading(null);
      setPendingPlan(null);
    }
  }, [dispatch, onPlanChanged, pendingPlan]);

  if (loadingPlans && plans.length === 0) {
    return <Spinner />;
  }

  const content = (
    <>
      {!embedded && !changePlan && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            <FormattedMessage id="Pricing.ChooseYourPlan" defaultMessage="Choose your plan" />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <FormattedMessage
              id="Pricing.PricingDescriptionNew"
              defaultMessage="Simple tiered pricing that grows with your feedback program."
            />
          </Typography>
        </Box>
      )}

      {changePlan && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            <FormattedMessage id="Billing.ChangePlan.Title" defaultMessage="Change plan" />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="Billing.ChangePlan.ProrationNote"
              defaultMessage="Plan changes are prorated. Your next invoice will reflect any credit or charge."
            />
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, mb: 5 }}>
        <ToggleButtonGroup
          exclusive
          value={interval}
          onChange={handleIntervalChange}
          aria-label="billing interval"
        >
          <ToggleButton value={INTERVALS.month}>
            <FormattedMessage id="Pricing.Billing.Monthly" defaultMessage="Monthly" />
          </ToggleButton>
          <ToggleButton value={INTERVALS.year}>
            <FormattedMessage
              id="Pricing.Billing.Annual"
              defaultMessage="Annual (save {months} months)"
              values={{ months: getAnnualSavingsMonths() }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={tierGridSx}>
        {TIERS.map(tier => {
          const tierVolumeIndex = volumeIndices[tier.id] ?? 0;
          const tierVolume = getTierVolumeAtIndex(tier, tierVolumeIndex);
          const people = tierVolume?.people ?? null;
          const pricing = getTierVolumePrice(tier, people, interval);
          const showContact = pricing?.contact || tierVolume?.contact;
          const selectedPlan = findPlanForSelection(plans, tier.id, people, interval);
          const isCurrentPlan = changePlan && selectedPlan?.id === currentPlanId;
          const isUnavailable = !selectedPlan && !showContact;

          return (
            <Card
              key={tier.id}
              variant="outlined"
              sx={tier.popular ? { ...tierCardSx, ...tierCardPopularSx } : tierCardSx}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" component="h3">
                    {formatPricingMessage(intl, tier.nameKey, tier.id)}
                  </Typography>
                  {tier.popular && (
                    <Chip
                      size="small"
                      color="primary"
                      label={
                        <FormattedMessage
                          id="PricingPlan.MostPopular"
                          defaultMessage="Most Popular"
                        />
                      }
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatPricingMessage(intl, tier.descriptionKey, 'Plan description')}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  {showContact ? (
                    <Typography variant="h4" component="p">
                      <FormattedMessage id="Pricing.ContactUsPrice" defaultMessage="Contact us" />
                    </Typography>
                  ) : pricing ? (
                    <>
                      <Typography variant="h4" component="span" sx={{ fontWeight: 'bold' }}>
                        €<FormattedNumber value={pricing.price} />
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 0.5 }}
                      >
                        {interval === INTERVALS.month ? (
                          <FormattedMessage id="Pricing.PerMonth" defaultMessage="/ month" />
                        ) : (
                          <FormattedMessage id="Pricing.PerYear" defaultMessage="/ year" />
                        )}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      <FormattedMessage
                        id="Pricing.NotAvailableAtVolume"
                        defaultMessage="Not available at this volume"
                      />
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={tierVolumeLabelsSx}>
                    <Typography component="span" sx={tierVolumeLabelTextSx}>
                      <FormattedMessage id="Pricing.Volume.Less" defaultMessage="Less volume" />
                    </Typography>
                    <Typography component="span" sx={tierVolumeLabelTextSx}>
                      <FormattedMessage id="Pricing.Volume.More" defaultMessage="More volume" />
                    </Typography>
                  </Box>
                  <Slider
                    value={tierVolumeIndex}
                    min={0}
                    max={Math.max(tier.volumes.length - 1, 0)}
                    step={1}
                    onChange={(_, value) => handleTierVolumeChange(tier.id, value)}
                    valueLabelDisplay="off"
                    disabled={tier.volumes.length <= 1}
                    size="small"
                  />
                  <Typography sx={tierVolumeValueSx} component="p">
                    {showContact ? (
                      <FormattedMessage id="Pricing.Volume.Unlimited" defaultMessage="Unlimited" />
                    ) : (
                      <>
                        <Box component="strong" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {formatPeople(pricing?.people ?? people, formatNumber)}
                        </Box>{' '}
                        <FormattedMessage
                          id="Pricing.Volume.PerMonthSuffix"
                          defaultMessage="people /mo"
                        />
                      </>
                    )}
                  </Typography>
                </Box>

                <Box component="ul" sx={{ pl: 2.5, mb: 3, flex: 1, mt: 0 }}>
                  {tier.featureKeys.map(featureKey => (
                    <Typography component="li" variant="body2" key={featureKey} sx={{ mb: 0.5 }}>
                      {formatPricingMessage(intl, featureKey, featureKey)}
                    </Typography>
                  ))}
                </Box>

                {!changePlan && !userId && !showContact && !isUnavailable ? (
                  <Button
                    variant={tier.popular ? 'contained' : 'outlined'}
                    fullWidth
                    component={Link}
                    to="/sign-up"
                  >
                    <FormattedMessage id="Nav.GetStarted" defaultMessage="Free trial" />
                  </Button>
                ) : (
                  <Button
                    variant={tier.popular ? 'contained' : 'outlined'}
                    fullWidth
                    disabled={isUnavailable || checkoutLoading === tier.id || isCurrentPlan}
                    onClick={showContact ? undefined : handleSelectTier(tier, people)}
                    {...(showContact
                      ? {
                          component: 'a',
                          href: 'mailto:hello@promoter.ninja?subject=Promoter%20Ninja%20Scale%20plan',
                        }
                      : {})}
                  >
                    {showContact ? (
                      <FormattedMessage id="Pricing.ContactUsPrice" defaultMessage="Contact us" />
                    ) : isCurrentPlan ? (
                      <FormattedMessage
                        id="Billing.ChangePlan.CurrentPlan"
                        defaultMessage="Current plan"
                      />
                    ) : changePlan ? (
                      <FormattedMessage
                        id="Billing.ChangePlan.Select"
                        defaultMessage="Change to this plan"
                      />
                    ) : (
                      <FormattedMessage id="Pricing.Select" defaultMessage="Select Plan" />
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {!changePlan && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="Pricing.ContactForCustomPlan"
              defaultMessage="If you don't see a plan that meets your needs, please {contact}."
              values={{
                contact: (
                  <a href="mailto:hello@promoter.ninja">
                    <FormattedMessage id="Pricing.ContactUs" defaultMessage="contact us" />
                  </a>
                ),
              }}
            />
          </Typography>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <FormattedMessage
            id="Billing.ChangePlan.ConfirmTitle"
            defaultMessage="Confirm plan change"
          />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage
              id="Billing.ChangePlan.ConfirmDescription"
              defaultMessage="Your subscription will switch to {planName}. Prorated charges or credits may apply on your next invoice."
              values={{ planName: pendingPlan?.name || pendingPlan?.id }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            <FormattedMessage id="Billing.ChangePlan.ConfirmCancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="contained" onClick={handleConfirmPlanChange}>
            <FormattedMessage id="Billing.ChangePlan.Confirm" defaultMessage="Confirm change" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (embedded) {
    return content;
  }

  return <Box sx={pricingTableSx}>{content}</Box>;
}

TierPricingPage.propTypes = {
  embedded: PropTypes.bool,
  changePlan: PropTypes.bool,
  currentPlanId: PropTypes.string,
  onPlanChanged: PropTypes.func,
};

TierPricingPage.defaultProps = {
  embedded: false,
  changePlan: false,
  currentPlanId: null,
  onPlanChanged: null,
};

export default TierPricingPage;
